/**
 * Barra unificada de legenda para Termos e Anotações.
 * Cada seção tem seu próprio grupo visual com fundo, separados claramente.
 */

import React, { useMemo } from 'react'
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import LabelIcon from '@mui/icons-material/Label'
import EditNoteIcon from '@mui/icons-material/EditNote'
import NoteIcon from '@mui/icons-material/Note'
import HighlightIcon from '@mui/icons-material/Highlight'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import TuneIcon from '@mui/icons-material/Tune'
import type { CustomTermConfig } from '../../../types/settings'
import type { UserAnnotations } from '../../../types/transcription'
import { getCategoryColor } from '../../../constants/termColors'

interface ToolsLegendBarProps {
  terms: CustomTermConfig[]
  termsVisible: boolean
  onToggleTermsVisibility: () => void
  onOpenTermsPanel: () => void
  annotations: UserAnnotations
  annotationsVisible: boolean
  onToggleAnnotationsVisibility: () => void
  onOpenAnnotationsPanel: () => void
}

/* Estilo compartilhado de cada seção-grupo */
const sectionSx = (active: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: '6px',
  bgcolor: active ? 'rgba(0,0,0,0.03)' : 'transparent',
  opacity: active ? 1 : 0.45,
  transition: 'all 0.2s',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.05)', opacity: active ? 1 : 0.7 },
})

export default function ToolsLegendBar({
  terms, termsVisible, onToggleTermsVisibility, onOpenTermsPanel,
  annotations, annotationsVisible, onToggleAnnotationsVisibility, onOpenAnnotationsPanel,
}: ToolsLegendBarProps) {
  const activeTerms = useMemo(() => terms.filter(t => t.enabled !== false), [terms])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const t of activeTerms) {
      const cat = t.category || 'geral'
      counts[cat] = (counts[cat] || 0) + 1
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1])
  }, [activeTerms])

  const annotationCounts = useMemo(() => ({
    notes: annotations?.items?.length || 0,
    highlights: annotations?.highlights?.length || 0,
    bookmarks: annotations?.bookmarks?.length || 0,
  }), [annotations])

  const totalAnnotations = annotationCounts.notes + annotationCounts.highlights + annotationCounts.bookmarks
  const hasTerms = activeTerms.length > 0
  const hasAnnotations = totalAnnotations > 0

  if (!hasTerms && !hasAnnotations) return null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 0.75,
        py: 0.5,
        mb: 1,
        bgcolor: '#f8f9fa',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        flexWrap: 'wrap',
        minHeight: 34,
      }}
    >
      {/* ─── Termos ─── */}
      {hasTerms && (
        <Box sx={sectionSx(termsVisible)}>
          <LabelIcon sx={{ fontSize: 15, color: termsVisible ? '#5c6bc0' : '#999' }} />
          <Typography variant="caption" sx={{ fontSize: '0.67rem', fontWeight: 600, color: termsVisible ? '#37474f' : '#999', whiteSpace: 'nowrap' }}>
            Termos
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.25, ml: 0.25 }}>
            {categoryCounts.slice(0, 4).map(([cat, count]) => {
              const color = getCategoryColor(cat)
              return (
                <Chip
                  key={cat}
                  label={`${color.label} ${count}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    bgcolor: termsVisible ? color.bg : '#eee',
                    color: termsVisible ? color.text : '#aaa',
                    transition: 'all 0.2s',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )
            })}
            {categoryCounts.length > 4 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: '18px' }}>
                +{categoryCounts.length - 4}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', ml: 0.25 }}>
            <Tooltip title={termsVisible ? 'Ocultar termos' : 'Mostrar termos'}>
              <IconButton size="small" onClick={onToggleTermsVisibility} sx={{ width: 22, height: 22 }}>
                {termsVisible ? <VisibilityIcon sx={{ fontSize: 14 }} /> : <VisibilityOffIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Gerenciar termos">
              <IconButton size="small" onClick={onOpenTermsPanel} sx={{ width: 22, height: 22 }}>
                <TuneIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* ─── Separador ─── */}
      {hasTerms && hasAnnotations && (
        <Box sx={{ width: '1px', height: 20, bgcolor: '#d0d0d0', mx: 0.25, flexShrink: 0 }} />
      )}

      {/* ─── Anotações ─── */}
      {hasAnnotations && (
        <Box sx={sectionSx(annotationsVisible)}>
          <EditNoteIcon sx={{ fontSize: 15, color: annotationsVisible ? '#ef6c00' : '#999' }} />
          <Typography variant="caption" sx={{ fontSize: '0.67rem', fontWeight: 600, color: annotationsVisible ? '#37474f' : '#999', whiteSpace: 'nowrap' }}>
            Notas
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.25, ml: 0.25 }}>
            {annotationCounts.notes > 0 && (
              <Chip
                icon={<NoteIcon sx={{ fontSize: '12px !important' }} />}
                label={annotationCounts.notes}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: annotationsVisible ? '#fff9c4' : '#eee',
                  color: annotationsVisible ? '#6d4c00' : '#aaa',
                  transition: 'all 0.2s',
                  '& .MuiChip-label': { px: 0.4 },
                  '& .MuiChip-icon': { ml: 0.25, mr: -0.25 },
                }}
              />
            )}
            {annotationCounts.highlights > 0 && (
              <Chip
                icon={<HighlightIcon sx={{ fontSize: '12px !important' }} />}
                label={annotationCounts.highlights}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: annotationsVisible ? '#e3f2fd' : '#eee',
                  color: annotationsVisible ? '#0d47a1' : '#aaa',
                  transition: 'all 0.2s',
                  '& .MuiChip-label': { px: 0.4 },
                  '& .MuiChip-icon': { ml: 0.25, mr: -0.25 },
                }}
              />
            )}
            {annotationCounts.bookmarks > 0 && (
              <Chip
                icon={<BookmarkIcon sx={{ fontSize: '12px !important' }} />}
                label={annotationCounts.bookmarks}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: annotationsVisible ? '#e8f5e9' : '#eee',
                  color: annotationsVisible ? '#1b5e20' : '#aaa',
                  transition: 'all 0.2s',
                  '& .MuiChip-label': { px: 0.4 },
                  '& .MuiChip-icon': { ml: 0.25, mr: -0.25 },
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', ml: 0.25 }}>
            <Tooltip title={annotationsVisible ? 'Ocultar anotações' : 'Mostrar anotações'}>
              <IconButton size="small" onClick={onToggleAnnotationsVisibility} sx={{ width: 22, height: 22 }}>
                {annotationsVisible ? <VisibilityIcon sx={{ fontSize: 14 }} /> : <VisibilityOffIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Gerenciar anotações">
              <IconButton size="small" onClick={onOpenAnnotationsPanel} sx={{ width: 22, height: 22 }}>
                <TuneIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  )
}
